import React from 'react';
import {
  AssistantIcon,
  CloseIcon,
  OpenInFullIcon,
  CloseFullscreenIcon,
} from '@/components/icons';
import {
  Paper,
  Box,
  Avatar,
  Stack,
  Typography,
  IconButton,
  Alert,
  useTheme,
  Unstable_TrapFocus,
} from '@mui/material';

import PanelResizeHandle from '@/components/panel-resize-handle/PanelResizeHandle';
import PanelHeightResizeHandle from '@/components/panel-height-resize-handle';
import { Message } from './Message';
import { useChatBot, useGetConfig } from './ChatBot';
import { ChatInput } from './ChatInput';
import { PrivacyNotice } from './PrivacyNotice';
import { ServiceUnavailableContent } from './ServiceUnavailableContent';
import { PoweredByCapio } from './PoweredByCapio';
import { IntakeFormCard } from './intake-form';
import { WelcomeMessage } from './WelcomeMessage';
import { QuickStartChips } from './QuickStartChips';
import { AnamAvatarPanel, AvatarToggle, useAvatarSession } from '../avatar';
import { useEffectiveQuickStartPlacement } from '../hooks/useEffectiveQuickStartPlacement';
import { getIsMobile } from '../utils/device';
import { trackWidgetEvent } from '../utils/analytics';
import {
  CHAT_WIDGET_WHATSAPP_DEFLECTION_CLICKED,
  CHAT_WIDGET_PANEL_RENDERED,
  CHAT_WIDGET_PANEL_EXPANDED,
  CHAT_WIDGET_PANEL_COLLAPSED,
} from '@/lib/analytics-events';
import {
  getViewportHeightBucket,
  PANEL_COMPACT_BORDER_RADIUS,
  PANEL_WIDTH_DEFAULT,
  PANEL_WIDTH_MIN,
  CHAT_WIDGET_PANEL_COMPACT_WIDTH_KEY,
  getCompactPanelDisplayWidth,
  getFootprintWidthFromCompactDisplay,
} from '@/lib/panel-resize';
import { usePersistedPanelWidth } from '@/lib/usePersistedPanelWidth';
import { scaledFontSize } from './createWidgetTheme';
import WhatsAppBrandIcon from '@/components/whatsapp-brand-icon';
import { DEFAULT_PRIVACY_NOTICE_TEXT } from '@/lib/privacy-notice';
import { ChatHeaderMenu } from './ChatHeaderMenu';
import { useTranscriptDownloadShown } from './useTranscriptDownloadShown';
import {
  buildWhatsAppLink,
  canShowWhatsAppDeflection,
  whatsappDeflectionHeaderButtonSx,
} from '@/lib/whatsapp-utils';
import { useLauncherEmbedStyle } from '../context/LauncherEmbedStyleContext';
import { useChatDrawerA11y } from './useChatDrawerA11y';
import { useDialogFocusLoop } from './useDialogFocusLoop';
import { useInertHostPage } from './useInertHostPage';
import { useCompactPanelLayout } from './useCompactPanelLayout';
import { ChatMinimizedBar } from './ChatMinimizedBar';
import {
  WidgetAnnouncerOutlet,
  useWidgetAnnouncer,
} from './WidgetAnnouncerProvider';
import { SkipToMessageInputLink } from './SkipToMessageInputLink';
import { visuallyHiddenSx } from './visuallyHiddenSx';
import { useWidgetI18n } from '../i18n/WidgetI18nProvider';

export function ChatDrawer() {
  const theme = useTheme();
  const { t, locale } = useWidgetI18n();
  const config = useGetConfig();
  const { mx, zIndex } = useLauncherEmbedStyle();
  const horizontalInset = mx * 2;
  const quickStartPlacement = useEffectiveQuickStartPlacement();
  const whatsappLink = canShowWhatsAppDeflection(
    config.whatsappPhoneNumber,
    config.whatsappDeflectionEnabled
  )
    ? buildWhatsAppLink(config.whatsappPhoneNumber!)
    : null;
  const [transcriptError, setTranscriptError] = React.useState(false);
  const [sessionOpenedAt, setSessionOpenedAt] = React.useState<number | null>(
    null
  );

  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const chatInputRef = React.useRef<
    HTMLInputElement | HTMLTextAreaElement | null
  >(null);
  const dialogRef = React.useRef<HTMLDivElement | null>(null);
  const prevHistoryLengthRef = React.useRef<number>(0);
  const {
    layout,
    dockModeEnabled,
    setLayout,
    panelWidth,
    setPanelWidth,
    history,
    open,
    state: chatState,
    toggleChatUi,
    isServiceDown,
    launcherRef,
    sessionId,
    threadId,
    engageId,
    mandatoryIntakeOnOpen,
    intakeCompleted,
    completeIntake,
  } = useChatBot();
  const showIntakeForm = mandatoryIntakeOnOpen && !intakeCompleted;

  // After intake submit the form unmounts — restore focus to the chat composer (WCAG).
  const wasShowingIntakeRef = React.useRef(showIntakeForm);
  React.useEffect(() => {
    if (wasShowingIntakeRef.current && !showIntakeForm) {
      queueMicrotask(() => chatInputRef.current?.focus());
    }
    wasShowingIntakeRef.current = showIntakeForm;
  }, [showIntakeForm]);

  const { panelWidth: compactFootprint, setPanelWidth: setCompactFootprint } =
    usePersistedPanelWidth(
      CHAT_WIDGET_PANEL_COMPACT_WIDTH_KEY,
      PANEL_WIDTH_DEFAULT
    );
  const { avatarMode, showAvatarToggle } = useAvatarSession();
  const { announce } = useWidgetAnnouncer();

  useTranscriptDownloadShown(
    config.transcriptDownloadEnabled ?? false,
    history,
    open
  );

  useChatDrawerA11y({
    open,
    agentName: config.agentName,
    history,
    chatState,
    isServiceDown,
    inputRef: chatInputRef,
    intakePending: showIntakeForm,
    dialogRef,
  });

  // Track if device is mobile to hide expand button
  const [isMobile, setIsMobile] = React.useState<boolean>(getIsMobile());

  // Track viewport dimensions for mobile keyboard handling (iOS/Android)
  const [viewportHeight, setViewportHeight] = React.useState<number | null>(
    null
  );
  const [viewportOffsetTop, setViewportOffsetTop] = React.useState<number>(0);
  const [windowHeight, setWindowHeight] = React.useState(() =>
    typeof window !== 'undefined' ? window.innerHeight : 800
  );

  const panelLayout = useCompactPanelLayout({
    chatHeight: config.chatHeight ?? 'full_height',
    isMobile,
    open,
    marginTop: config.chatBoxMarginTop || 0,
    mobileViewportHeight: viewportHeight,
    mobileViewportOffsetTop: viewportOffsetTop,
    windowHeight,
  });

  useDialogFocusLoop(dialogRef, open);
  // Only the phone layout is full screen for this purpose; the desktop wider view is not.
  useInertHostPage(dialogRef, open && isMobile, zIndex);

  const prevOpenRef = React.useRef<boolean | undefined>(undefined);
  const prevLayoutRef = React.useRef<
    'docked' | 'fullscreen' | 'minimized' | undefined
  >(undefined);

  const returnToMinimizedBar = React.useCallback(() => {
    // Keep intake mounted: minimized swaps to ChatMinimizedBar and would wipe the form.
    if (showIntakeForm) {
      setLayout('docked');
      return;
    }
    // open stays true; reset compact height so bar send reopens the floating panel.
    panelLayout.collapse();
    trackWidgetEvent('Chat Widget - Chat wider view minimized');
    setLayout('minimized');
  }, [panelLayout, setLayout, showIntakeForm]);

  React.useEffect(() => {
    const wasOpen = prevOpenRef.current;
    const prevLayout = prevLayoutRef.current;

    if (open && wasOpen !== true) {
      setSessionOpenedAt(Date.now());
      if (!isMobile && layout === 'docked') {
        trackWidgetEvent(CHAT_WIDGET_PANEL_RENDERED, {
          height_mode: panelLayout.heightMode,
          viewport_height_bucket: getViewportHeightBucket(windowHeight),
        });
      }
    }

    if (
      !isMobile &&
      open &&
      wasOpen === true &&
      layout === 'docked' &&
      prevLayout === 'minimized'
    ) {
      trackWidgetEvent(CHAT_WIDGET_PANEL_RENDERED, {
        height_mode: panelLayout.heightMode,
        viewport_height_bucket: getViewportHeightBucket(windowHeight),
      });
    }

    if (wasOpen === true && !open) {
      setSessionOpenedAt(null);
      setTranscriptError(false);
      queueMicrotask(() => launcherRef.current?.focus());
    }

    prevOpenRef.current = open;
    prevLayoutRef.current = layout;
  }, [
    open,
    launcherRef,
    isMobile,
    layout,
    panelLayout.heightMode,
    panelLayout.isExpanded,
    windowHeight,
  ]);

  // Update mobile state and desktop viewport height on resize
  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(getIsMobile());
      setWindowHeight(window.innerHeight);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle visualViewport resize for mobile keyboard (iOS Safari, Android Chrome)
  React.useEffect(() => {
    if (!isMobile) {
      setViewportHeight(null);
      setViewportOffsetTop(0);
      return;
    }

    const viewport = window.visualViewport;
    if (!viewport) return;

    const handleViewportResize = () => {
      // Use visualViewport height which accounts for on-screen keyboard
      setViewportHeight(viewport.height);
      // Track offset for iOS where page scrolls when keyboard opens
      setViewportOffsetTop(viewport.offsetTop);
    };

    // Set initial values
    handleViewportResize();

    viewport.addEventListener('resize', handleViewportResize);
    viewport.addEventListener('scroll', handleViewportResize);

    return () => {
      viewport.removeEventListener('resize', handleViewportResize);
      viewport.removeEventListener('scroll', handleViewportResize);
    };
  }, [isMobile]);

  React.useEffect(() => {
    const bottom = scrollRef.current;
    if (!bottom) return;

    const messagesContainer = bottom.parentElement;
    if (!messagesContainer) return;

    // Check if a new message was added (not just streaming update)
    const isNewMessage = history.length > prevHistoryLengthRef.current;
    prevHistoryLengthRef.current = history.length;

    // Check if user is near bottom (within 100px)
    const isNearBottom =
      messagesContainer.scrollHeight -
        messagesContainer.scrollTop -
        messagesContainer.clientHeight <
      100;

    // Always scroll for new messages, or if user is near bottom (for streaming updates)
    if (isNewMessage || isNearBottom) {
      bottom.scrollIntoView({ behavior: 'auto', block: 'end' });
    }
  }, [history]);

  // A11y - Handle Escape key to close chat
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      // Only handle if chat is open and no other input is focused
      // A popup (country list) that already handled Escape owns it.
      if (e.key === 'Escape' && open && !e.defaultPrevented) {
        // Don't interfere if user is typing in another input field
        const target = e.target as HTMLElement;
        const isInputField =
          target.tagName === 'INPUT' || target.tagName === 'TEXTAREA';
        const isOurWidget = target.closest('capio-engage-widget') !== null;

        // Only close if escape is pressed outside input fields or within our widget
        if (!isInputField || isOurWidget) {
          if (dockModeEnabled && layout === 'fullscreen' && !getIsMobile()) {
            returnToMinimizedBar();
            return;
          }
          toggleChatUi(false);
        }
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [dockModeEnabled, layout, open, returnToMinimizedBar, toggleChatUi]);

  // When mandatory intake is pending, never stay on the minimized bar — expand so the form can render.
  React.useEffect(() => {
    if (
      open &&
      showIntakeForm &&
      dockModeEnabled &&
      layout === 'minimized' &&
      !isMobile
    ) {
      setLayout('docked');
    }
  }, [open, showIntakeForm, dockModeEnabled, layout, isMobile, setLayout]);

  if (!open) return null;

  // While intake is pending, never mount the minimized bar — that unmounts IntakeFormCard.
  if (
    dockModeEnabled &&
    layout === 'minimized' &&
    !isMobile &&
    !showIntakeForm
  ) {
    return <ChatMinimizedBar chatInputRef={chatInputRef} />;
  }

  const isFullscreenDock =
    dockModeEnabled && layout === 'fullscreen' && !isMobile;
  const marginTop = config.chatBoxMarginTop || 0;

  const getFullscreenHeight = () => {
    if (isMobile && viewportHeight !== null) {
      return `${viewportHeight}px`;
    }
    if (marginTop > 0) {
      return `calc(100dvh - ${marginTop}px)`;
    }
    return '100dvh';
  };

  const getFullscreenTop = () => {
    if (isMobile && viewportHeight !== null) {
      return `${viewportOffsetTop + marginTop}px`;
    }
    return marginTop > 0 ? `${marginTop}px` : 0;
  };

  const isCompactFloating = !isFullscreenDock && panelLayout.borderRadius > 0;
  const compactRightInset =
    isCompactFloating && typeof panelLayout.right === 'number'
      ? panelLayout.right
      : 0;
  const compactLayoutFootprint = isCompactFloating
    ? compactFootprint
    : panelWidth;
  const desktopWidth = isFullscreenDock
    ? 840
    : isCompactFloating
      ? getCompactPanelDisplayWidth(compactLayoutFootprint, compactRightInset)
      : panelWidth;
  const showWidthResizeHandle = !isMobile && layout === 'docked';
  const compactWidthMin = Math.max(0, PANEL_WIDTH_MIN - compactRightInset);

  const handleCollapseToDocked = () => {
    trackWidgetEvent('Chat Widget - Chat wider view collapsed to side panel');
    setLayout('docked');
  };

  const handleCloseChat = () => {
    if (isFullscreenDock) {
      returnToMinimizedBar();
      return;
    }
    trackWidgetEvent('Chat Widget - Chat side panel minimized');
    toggleChatUi(false);
  };

  const resetCompactFootprint = () => {
    setCompactFootprint(PANEL_WIDTH_DEFAULT);
  };

  const handleExpandPanel = () => {
    resetCompactFootprint();
    setPanelWidth(PANEL_WIDTH_DEFAULT);
    panelLayout.expand();
    trackWidgetEvent(CHAT_WIDGET_PANEL_EXPANDED, { from_mode: 'compact' });
    announce(t('announce.expandedFullHeight'), { lang: locale });
  };

  const handleCollapsePanel = () => {
    resetCompactFootprint();
    panelLayout.collapse();
    trackWidgetEvent(CHAT_WIDGET_PANEL_COLLAPSED, { to_mode: 'compact' });
    announce(t('announce.collapsedCompactHeight'), { lang: locale });
  };

  return (
    // Enforce uses document.activeElement, which is the widget host when
    // focus is inside the shadow tree, so it always thinks focus escaped
    // and steals it back to this Paper. useDialogFocusLoop does the Tab loop instead.
    <Unstable_TrapFocus
      open
      disableAutoFocus
      disableRestoreFocus
      disableEnforceFocus
    >
      <Paper
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="chat-agent-name"
        sx={{
          zIndex,
          width: isMobile ? '100%' : desktopWidth,
          right: isMobile
            ? 0
            : isFullscreenDock
              ? 'calc(50% - 420px)'
              : panelLayout.right,
          left: isMobile ? 0 : 'auto',
          top: isFullscreenDock ? getFullscreenTop() : panelLayout.top,
          bottom: isFullscreenDock ? 'auto' : panelLayout.bottom,
          position: 'fixed',
          maxWidth: isMobile ? '100%' : `calc(100vw - ${horizontalInset}px)`,
          height: isFullscreenDock ? getFullscreenHeight() : panelLayout.height,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: isCompactFloating
            ? `${PANEL_COMPACT_BORDER_RADIUS}px`
            : 0,
          overflow: 'visible',
          boxShadow: isMobile
            ? 'none'
            : isCompactFloating
              ? '0 12px 32px rgba(0,0,0,0.35)'
              : '0 12px 32px rgba(0,0,0,0.35)',
          border: isMobile || isCompactFloating ? 'none' : '1px solid',
          borderLeft: showWidthResizeHandle ? 'none' : undefined,
          borderColor: 'grey.300',
          transition: isMobile ? 'none' : 'height 0.1s ease-out',
          backdropFilter: 'blur(6px)',
          bgcolor: theme.palette.background.default,
        }}
      >
        <WidgetAnnouncerOutlet />
        {!isServiceDown && (
          <SkipToMessageInputLink
            inputRef={chatInputRef}
            intakePending={showIntakeForm}
          />
        )}
        <Stack
          sx={{
            height: '100%',
            ...(isCompactFloating && {
              borderRadius: `${PANEL_COMPACT_BORDER_RADIUS}px`,
              overflow: 'hidden',
            }),
          }}
          alignContent="flex-start"
          justifyContent="space-between"
        >
          {/* Header - fixed at top, never shrinks */}
          <Box
            component="header"
            role="region"
            aria-labelledby="chat-header-label"
            sx={{
              px: 2,
              py: 1.25,
              display: 'flex',
              alignItems: 'center',
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              justifyContent: 'space-between',
              flexShrink: 0,
              position: 'sticky',
              top: 0,
              zIndex: 11,
              overflow: 'visible',
            }}
          >
            <Typography
              component="span"
              id="chat-header-label"
              lang={locale}
              sx={visuallyHiddenSx}
            >
              {t('drawer.header')}
            </Typography>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              alignContent="space-between"
              justifyContent="space-between"
              width="100%"
            >
              <Stack direction="row" gap={2} alignItems="center">
                <Avatar
                  variant="circular"
                  src={config.logo}
                  alt={config.agentName}
                  sx={{
                    bgcolor: theme.palette.primary.contrastText,
                    color: theme.palette.primary.main,
                  }}
                >
                  {!config.logo && <AssistantIcon aria-hidden />}
                </Avatar>
                <Typography
                  component="h1"
                  variant="h6"
                  id="chat-agent-name"
                  sx={{
                    m: 0,
                    fontSize: scaledFontSize(theme, 20),
                  }}
                >
                  {config.agentName}
                </Typography>
              </Stack>
              {/* Trailing actions: channel CTA, overflow menu, dismiss. */}
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                role="group"
                aria-labelledby="chat-window-controls-label"
              >
                <Typography
                  component="span"
                  id="chat-window-controls-label"
                  lang={locale}
                  sx={visuallyHiddenSx}
                >
                  {t('drawer.windowControls')}
                </Typography>
                {whatsappLink && (
                  <IconButton
                    size="small"
                    disableRipple
                    aria-label={t('drawer.continueWhatsApp')}
                    title={t('drawer.continueWhatsAppTitle')}
                    lang={locale}
                    onClick={() => {
                      trackWidgetEvent(
                        CHAT_WIDGET_WHATSAPP_DEFLECTION_CLICKED,
                        {
                          deviceType: isMobile ? 'mobile' : 'desktop',
                        }
                      );
                      window.open(
                        whatsappLink,
                        '_blank',
                        'noopener,noreferrer'
                      );
                    }}
                    sx={whatsappDeflectionHeaderButtonSx}
                  >
                    <WhatsAppBrandIcon size={20} aria-hidden />
                  </IconButton>
                )}
                <ChatHeaderMenu
                  transcriptDownloadEnabled={
                    config.transcriptDownloadEnabled ?? false
                  }
                  institutionName={config.institutionName || config.agentName}
                  agentName={config.agentName}
                  history={history}
                  sessionOpenedAt={sessionOpenedAt}
                  onTranscriptError={() => setTranscriptError(true)}
                  fontScale={size => scaledFontSize(theme, size)}
                />
                {dockModeEnabled &&
                  !isMobile &&
                  layout === 'docked' &&
                  panelLayout.heightMode === 'full height' && (
                    <IconButton
                      size="small"
                      aria-label={t('drawer.expandFullscreen')}
                      title={t('drawer.expandFullscreenTitle')}
                      lang={locale}
                      sx={{ color: 'inherit' }}
                      onClick={() => {
                        trackWidgetEvent(
                          'Chat Widget - Chat side panel expanded to wider view'
                        );
                        setLayout('fullscreen');
                      }}
                    >
                      <OpenInFullIcon
                        fontSize="small"
                        sx={{ fontSize: scaledFontSize(theme, 16) }}
                        aria-hidden
                      />
                    </IconButton>
                  )}
                {layout === 'docked' &&
                  panelLayout.showExpandControl &&
                  !panelLayout.isExpanded && (
                    <IconButton
                      size="small"
                      aria-label={t('drawer.expandFullHeight')}
                      title={t('drawer.expandFullHeightTitle')}
                      lang={locale}
                      sx={{ color: 'inherit', minWidth: 44, minHeight: 44 }}
                      onClick={handleExpandPanel}
                    >
                      <OpenInFullIcon
                        fontSize="small"
                        sx={{ fontSize: scaledFontSize(theme, 16) }}
                        aria-hidden
                      />
                    </IconButton>
                  )}
                {layout === 'docked' &&
                  panelLayout.showExpandControl &&
                  panelLayout.isExpanded && (
                    <IconButton
                      size="small"
                      aria-label={t('drawer.collapseCompactHeight')}
                      title={t('drawer.collapseCompactHeightTitle')}
                      lang={locale}
                      sx={{ color: 'inherit', minWidth: 44, minHeight: 44 }}
                      onClick={handleCollapsePanel}
                    >
                      <CloseFullscreenIcon
                        fontSize="small"
                        sx={{ fontSize: scaledFontSize(theme, 16) }}
                        aria-hidden
                      />
                    </IconButton>
                  )}
                {isFullscreenDock && (
                  <IconButton
                    size="small"
                    aria-label={t('drawer.exitFullscreen')}
                    title={t('drawer.exitFullscreenTitle')}
                    lang={locale}
                    edge="end"
                    sx={{ color: 'inherit' }}
                    onClick={handleCollapseToDocked}
                  >
                    <CloseFullscreenIcon
                      fontSize="small"
                      sx={{ fontSize: scaledFontSize(theme, 16) }}
                      aria-hidden
                    />
                  </IconButton>
                )}
                <IconButton
                  size="small"
                  aria-label={t('drawer.closeChat')}
                  title={t('drawer.closeChatTitle')}
                  lang={locale}
                  edge="end"
                  sx={{ color: 'inherit' }}
                  onClick={handleCloseChat}
                >
                  <CloseIcon
                    fontSize="small"
                    sx={{ fontSize: scaledFontSize(theme, 16) }}
                    aria-hidden
                  />
                </IconButton>
              </Stack>
            </Stack>
          </Box>

          {/* Content area - show error or normal chat */}
          {isServiceDown ? (
            <>
              {/* Error content */}
              <ServiceUnavailableContent
                message={config.serviceUnavailableMessage}
                supportEmail={config.supportEmail}
                supportPhone={config.supportPhone}
                supportLinkUrl={config.supportLinkUrl}
                supportLinkLabel={config.supportLinkLabel}
              />

              {/* Footer with "Powered by Capio" for service down state */}
              {config.showPoweredBy && (
                <Box
                  component="footer"
                  data-testid="chat-footer-container"
                  role="contentinfo"
                  aria-labelledby="chat-footer-label-outage"
                  sx={{
                    py: 2,
                    px: 2,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: 'background.paper',
                    // Safe area inset for iOS devices (notch, home indicator)
                    pb: isMobile ? 'max(16px, env(safe-area-inset-bottom))' : 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography
                    component="span"
                    id="chat-footer-label-outage"
                    lang={locale}
                    sx={visuallyHiddenSx}
                  >
                    {t('drawer.footer')}
                  </Typography>
                  <PoweredByCapio
                    fontSize={scaledFontSize(theme, 10)}
                    textAlign="center"
                  />
                </Box>
              )}
            </>
          ) : (
            <>
              {avatarMode && <AnamAvatarPanel />}
              {/* When avatarMode is on, the toggle is rendered as an overlay
                INSIDE AnamAvatarPanel (top-right of the avatar video), so
                we hide this in-flow row to avoid duplication and to free
                up vertical space for the messages list. */}
              {!avatarMode && showAvatarToggle && <AvatarToggle />}
              {/* Messages - scrollable area that takes remaining space */}
              <Box
                component="section"
                role="region"
                aria-labelledby="chat-messages-heading"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  flex: 1,
                  minHeight: 0,
                }}
              >
                <Typography
                  component="h2"
                  id="chat-messages-heading"
                  lang={locale}
                  sx={visuallyHiddenSx}
                >
                  {t('drawer.messages')}
                </Typography>
                {/* role="log" implies aria-live="polite". Streaming tokens would
                  otherwise be announced word-by-word (and timestamps with them).
                  WidgetAnnouncerProvider owns the single completion announcement. */}
                <div
                  style={{
                    overflow: 'auto',
                    flex: 1,
                    minHeight: 0,
                    WebkitOverflowScrolling: 'touch',
                  }}
                  ref={containerRef}
                >
                  <PrivacyNotice
                    noticeText={
                      config.privacyNoticeText?.trim() ||
                      DEFAULT_PRIVACY_NOTICE_TEXT
                    }
                    policyUrl={config.privacyPolicyUrl}
                  />
                  {showIntakeForm ? (
                    <IntakeFormCard
                      engageId={engageId}
                      sessionId={sessionId}
                      threadId={threadId}
                      onCompleted={completeIntake}
                    />
                  ) : (
                    <>
                      {/* role="log" wraps only the transcript so the privacy notice stays
                    a navigable landmark/heading outside live-region semantics. */}
                      <div
                        role="log"
                        aria-live="off"
                        aria-labelledby="chat-messages-heading"
                      >
                        <WelcomeMessage />
                        {quickStartPlacement === 'within_chat' &&
                          config.quickStartButtons &&
                          config.quickStartButtons.length > 0 && (
                            <QuickStartChips />
                          )}

                        <Stack sx={{ p: 3 }} gap={2} spacing={0.2}>
                          {history.map(msg => (
                            <Message
                              msg={msg}
                              history={history}
                              key={msg.timestamp}
                            />
                          ))}
                          <div ref={scrollRef} />
                        </Stack>
                      </div>
                    </>
                  )}
                </div>
              </Box>

              {/* INPUT - fixed at bottom, never shrinks */}
              <Stack
                sx={{
                  width: '100%',
                  bgcolor: theme.palette.background.paper,
                  py: 2,
                  px: 2,
                  flexShrink: 0,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  // Safe area inset for iOS devices (notch, home indicator)
                  pb: isMobile ? 'max(16px, env(safe-area-inset-bottom))' : 2,
                }}
                spacing={1}
                gap={1}
                alignItems="center"
                justifyContent="center"
              >
                <Box
                  sx={{ width: '100%' }}
                  role="region"
                  aria-labelledby="chat-input-heading"
                >
                  <Typography
                    component="h3"
                    id="chat-input-heading"
                    lang={locale}
                    sx={visuallyHiddenSx}
                  >
                    {t('drawer.messageInputRegion')}
                  </Typography>
                  {transcriptError && (
                    <Alert
                      severity="error"
                      sx={{ mb: 1 }}
                      onClose={() => setTranscriptError(false)}
                    >
                      Couldn&apos;t download your transcript — please try again.
                    </Alert>
                  )}
                  <ChatInput inputRef={chatInputRef} locked={showIntakeForm} />
                </Box>
                {/* Footer with disclaimer and "Powered by Capio" */}
                <Box
                  component="footer"
                  data-testid="chat-footer-container"
                  role="contentinfo"
                  aria-labelledby="chat-footer-label"
                  sx={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    justifyContent:
                      config.footerText && config.showPoweredBy
                        ? 'space-between'
                        : config.footerText
                          ? 'flex-start'
                          : config.showPoweredBy
                            ? 'center'
                            : 'flex-start',
                  }}
                >
                  <Typography
                    component="span"
                    id="chat-footer-label"
                    lang={locale}
                    sx={visuallyHiddenSx}
                  >
                    {t('drawer.footer')}
                  </Typography>
                  {config.footerText && (
                    <Typography
                      sx={{
                        color: 'text.disabled',
                        fontSize: scaledFontSize(theme, 10),
                        textAlign: 'left',
                        flex: 1,
                        wordWrap: 'break-word',
                      }}
                    >
                      {config.footerText}
                    </Typography>
                  )}
                  {config.showPoweredBy && (
                    <PoweredByCapio
                      fontSize={scaledFontSize(theme, 10)}
                      textAlign={config.footerText ? 'right' : 'center'}
                      sx={{ flexShrink: 0 }}
                    />
                  )}
                </Box>
              </Stack>
            </>
          )}
        </Stack>
        {/* Pointer-only drag affordance rendered last: no tab stop and hidden from
          assistive tech. Panel width is adjustable via mouse/touch only. */}
        {showWidthResizeHandle && (
          <PanelResizeHandle
            value={desktopWidth}
            onWidthChange={width => {
              if (isCompactFloating) {
                setCompactFootprint(
                  getFootprintWidthFromCompactDisplay(width, compactRightInset)
                );
                return;
              }
              setPanelWidth(width);
            }}
            min={isCompactFloating ? compactWidthMin : undefined}
            rightInset={compactRightInset}
            ariaLabel={t('drawer.resizePanel')}
            formatValueText={count =>
              t('drawer.resizePanelValueText', { count })
            }
            keyboardResizable={false}
          />
        )}
        {layout === 'docked' && panelLayout.showHeightResize && (
          <PanelHeightResizeHandle
            value={panelLayout.compactHeightPx}
            onHeightChange={panelLayout.setCompactHeight}
            maxHeight={panelLayout.maxCompactHeight}
            bottomInset={
              typeof panelLayout.bottom === 'number' ? panelLayout.bottom : 0
            }
            ariaLabel={t('drawer.resizeHeight')}
            formatValueText={count =>
              t('drawer.resizeHeightValueText', { count })
            }
            keyboardResizable={false}
          />
        )}
      </Paper>
    </Unstable_TrapFocus>
  );
}
